import React, { useState } from 'react';

import { AuthTags } from '~constants/authTags';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ErrorResponse, Spinner } from '~components';
import { TableView } from '~components/table-view';

import { SahReferenceDataRefreshResult } from '~support-at-home/models/sahReferenceData';

import { sahReferenceDataColumns } from '~support-at-home/forms/reference-data/sahReferenceDataColumns';

import { useGetSahReferenceDataQuery, useRefreshSahReferenceDataMutation } from '~support-at-home/actions/sahReferenceData';

const SPINNER_SIZE = 16;

const SahReferenceDataManager: React.FC = () => {
  const { data, isLoading, isError, error } = useGetSahReferenceDataQuery();
  const [refreshAll] = useRefreshSahReferenceDataMutation();
  const { showToast } = useToast();

  // "Refresh All" is the only refresh trigger left (the per-row Actions column was removed in
  // TAV-2589), so a single boolean is enough. It still matters: the lists are global rather than
  // per-company, so two concurrent refreshes could race on the same rows.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const runRefresh = async (action: () => Promise<SahReferenceDataRefreshResult>) => {
    if (isRefreshing) return; // debounce: no re-entry while a refresh is in flight
    setIsRefreshing(true);
    setErrorMessage(null);
    try {
      const result = await action();
      // The endpoint returns HTTP 200 even when one list's fetch/import failed — `success: false`
      // on the body (not the status code) is how a partial failure is signalled, so per-list
      // failures must be read out of `result.results`, not inferred from a thrown error.
      if (!result.success) {
        const failures = result.results.filter((entry) => !entry.success);
        const message = failures.length
          // `error` is optional on SahReferenceDataRefreshResult, so a failed entry that arrives
          // without one would otherwise render the literal "undefined" in both the toast and the
          // error panel.
          ? failures.map((entry) => `${entry.api_tag}: ${entry.error || 'Refresh failed'}`).join('; ')
          : 'Refresh reported a failure';
        setErrorMessage(message);
        showToast('danger', message);
      } else {
        showToast('success', 'Reference data refreshed');
      }
    } catch (err) {
      const message = getErrorMessage(err);
      setErrorMessage(message);
      showToast('danger', message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefreshAll = () => runRefresh(() => refreshAll().unwrap());

  if (isLoading) return <Spinner loading className="p-5" />;
  if (isError) return <ErrorResponse message={getErrorMessage(error)} />;

  const rows = data ?? [];

  return (
    <div data-cy="sah-reference-data">
      {errorMessage && <ErrorResponse message={errorMessage} />}
      {/* This tab shows the GLOBAL Services Australia reference lists (shared by every provider) —
          distinct from the per-client "SaH Approved Services" config tab, which manages which
          services are associated to an individual client. Easy to conflate by name; they are not
          the same data. */}
      <p className="text-muted">
        Global Services Australia reference lists shared by all providers. This is not the per-client SaH Approved
        Services list.
      </p>
      <div className="d-flex justify-content-end mb-2">
        <Button
          color="success"
          size="sm"
          authTag={AuthTags.SUPPORTATHOME.SERVICES.UPDATE}
          onClick={handleRefreshAll}
          disabled={isRefreshing}
          aria-label="Refresh all reference data lists"
          data-cy="sah-reference-data-refresh-all-btn"
        >
          {/* Composing Spinner + label as children rather than Button's `loading` prop — `loading`
              replaces children with a spinner and would drop this accessible name. */}
          {isRefreshing && <Spinner type="clip" size={SPINNER_SIZE} loading className="me-2" />}
          {isRefreshing ? 'Refreshing…' : 'Refresh All'}
        </Button>
      </div>
      <TableView
        id="sah-reference-data"
        data={rows}
        columns={sahReferenceDataColumns}
        readonly
        compactView
        bordered
        emptyMessage="No reference data lists found"
      />
    </div>
  );
};

export default SahReferenceDataManager;
